import { ArrowBack, Visibility } from '@mui/icons-material';
import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import ModalDialog from 'components/ModalDialog';
import { ModuleGuard } from 'components/ModuleGuard';
import { useAtomValue } from 'jotai';
import { ModuleKey } from 'lib/api/company/types';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import moment from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Alert, Card, CardBody, CardHeader } from 'reactstrap';

import {
  formatKPIValue,
  getKPIErrorMessage,
  getMetricOutcome,
  getSectionStatus,
  KPIOutcome,
} from './helper';
import KPIDashboardUnavailable from './KPIDashboardUnavailable';
import KPISectionCharts from './KPISectionCharts';
import KPISectionIcon from './KPISectionIcon';
import { kpiDashboardDataAtom } from './state';
import styles from './styles.module.css';
import { KPIHistoryData, KPIHistoryPoint, KPIMetric } from './types';

const outcomeDetails: Record<KPIOutcome, { label: string; className: string }> =
  {
    GOOD: { label: 'Improving', className: styles.trafficGood },
    BAD: { label: 'Worsening', className: styles.trafficBad },
    NEUTRAL: { label: 'Stable', className: styles.trafficNeutral },
  };

const KPI_HISTORY_DETAIL_LIMIT = 52;
const KPI_HISTORY_PAGE_SIZE = 10;

const DEFAULT_KPI_DETAILS_COLUMN_KEYS = [
  'label',
  'previous',
  'current',
  'change',
  'trend',
  'status',
  'source',
  'reportingDate',
  'actions',
];

const formatChange = (
  current: number | null,
  previous: number | null,
  unit: 'count' | 'days' | 'percent',
) => {
  if (current === null || previous === null) return 'Not available';
  const difference = current - previous;
  const formattedDifference = formatKPIValue(
    unit === 'percent' ? Math.abs(difference) : difference,
    unit,
  );
  const signedDifference =
    unit !== 'percent' && difference > 0
      ? `+${formattedDifference}`
      : formattedDifference;
  if (previous === 0) return signedDifference;
  // Trend and Status already communicate direction, so percentage values show
  // magnitude only and do not repeat a plus or minus sign.
  const percentage = Math.abs((difference / Math.abs(previous)) * 100).toFixed(
    1,
  );
  return `${signedDifference} (${percentage}%)`;
};

interface KPIDataGridRow {
  current: string;
  change: string;
  key: string;
  label: string;
  metric: KPIMetric;
  previous: string;
  reportingDate: string;
  reportingDateValue: string;
  source: string;
  statusSort: number;
  trend: string;
}

type KPIDataGridSortKey = 'label' | 'reportingDateValue' | 'statusSort';

const KPI_EXPORT_COLUMNS = [
  { key: 'label', label: 'KPI' },
  { key: 'previous', label: 'Previous' },
  { key: 'current', label: 'Current' },
  { key: 'change', label: 'Change' },
  { key: 'trend', label: 'Trend' },
  { key: 'status', label: 'Status' },
  { key: 'source', label: 'Source' },
  { key: 'reportingDate', label: 'Reporting Date' },
] as const;

const getMetricStatus = (metric: KPIMetric) => {
  const outcome = getMetricOutcome(metric);
  const outcomeDetail = outcomeDetails[outcome];
  return {
    label: outcomeDetail.label,
    sortValue: outcome === 'BAD' ? 0 : outcome === 'NEUTRAL' ? 1 : 2,
  };
};

interface KPISectionDataGridProps {
  companyId: number;
  metrics: KPIMetric[];
}

const KPISectionDataGrid = ({
  companyId,
  metrics,
}: KPISectionDataGridProps) => {
  // Use the shared DataGrid so KPI details follow the same table behavior and
  // visual conventions as the Policy Compliance module.
  const data = useMemo<KPIDataGridRow[]>(
    () =>
      metrics.map((metric) => ({
        change: formatChange(metric.value, metric.previous_value, metric.unit),
        current: formatKPIValue(metric.value, metric.unit),
        key: metric.key,
        label: metric.label,
        metric,
        previous: formatKPIValue(metric.previous_value, metric.unit),
        reportingDate: metric.period_end
          ? moment(metric.period_end).format('MMM D, YYYY')
          : 'Not available',
        reportingDateValue: metric.period_end || '',
        source: metric.source,
        statusSort: getMetricStatus(metric).sortValue,
        trend:
          metric.trend === 'NOT_AVAILABLE' ? 'Not available' : metric.trend,
      })),
    [metrics],
  );
  const [selectedMetric, setSelectedMetric] = useState<KPIMetric | null>(null);
  const [selectedMetricHistory, setSelectedMetricHistory] = useState<
    KPIHistoryPoint[]
  >([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const historyRequestId = useRef(0);
  const [orderBy, setOrderBy] = useState<KPIDataGridSortKey>('label');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [enabledColumnKeys, setEnabledColumnKeys] = useState(
    DEFAULT_KPI_DETAILS_COLUMN_KEYS,
  );
  const loadMetricHistory = useCallback(
    async (metric: KPIMetric) => {
      const requestId = ++historyRequestId.current;
      setSelectedMetric(metric);
      setSelectedMetricHistory([]);
      setHistoryPage(0);
      setHistoryLoading(true);

      try {
        // Dashboard charts intentionally load seven points. Fetch the API's full
        // supported history only when requested so the main dashboard stays fast.
        const response = await callApi(
          'dashboard/kpi/history/',
          'GET',
          { company_id: companyId, limit: KPI_HISTORY_DETAIL_LIMIT },
          null,
          null,
          'dashboard/kpi/history/:kpi-metric-detail',
        );
        if (requestId !== historyRequestId.current || !response?.data) return;

        const history = response.data as KPIHistoryData;
        const points = history.periods.flatMap((period) =>
          period.metrics
            .filter((historyMetric) => historyMetric.metric_key === metric.key)
            .map((historyMetric) => ({
              period_end: period.period_end,
              value: historyMetric.value,
            })),
        );
        setSelectedMetricHistory(points);
      } catch (error) {
        if (requestId === historyRequestId.current) {
          showFailureToast(
            getKPIErrorMessage(error, 'Unable to load KPI history.'),
          );
        }
      } finally {
        if (requestId === historyRequestId.current) setHistoryLoading(false);
      }
    },
    [companyId],
  );

  const closeMetricHistory = () => {
    // Ignore a late response after the user closes the modal or opens another KPI.
    historyRequestId.current += 1;
    setSelectedMetric(null);
    setSelectedMetricHistory([]);
    setHistoryPage(0);
    setHistoryLoading(false);
  };

  const config = useMemo<DataGridConfig>(
    () => ({
      rowIdKey: 'key',
      columns: [
        {
          key: 'label',
          label: 'KPI',
          headerTooltip: 'KPI included in this dashboard area',
          rowCellStyle: { fontWeight: 500 },
          sortable: true,
          width: '240px',
        },
        { key: 'previous', label: 'Previous', width: '100px' },
        {
          key: 'current',
          label: 'Current',
          rowCellClassName: styles.detailCurrentValue,
          width: '100px',
        },
        { key: 'change', label: 'Change', width: '150px' },
        { key: 'trend', label: 'Trend', width: '110px' },
        {
          key: 'status',
          label: 'Status',
          orderBy: 'statusSort',
          sortable: true,
          width: '135px',
          overrideContents: (rowData) => {
            const metric = rowData.metric as KPIMetric;
            const outcome = getMetricOutcome(metric);
            const outcomeDetail = outcomeDetails[outcome];
            const outcomeLabel = getMetricStatus(metric).label;
            return (
              <span className={styles.detailOutcome}>
                <span
                  className={`${styles.trafficLight} ${outcomeDetail.className}`}
                  aria-hidden="true"
                />
                {outcomeLabel}
              </span>
            );
          },
        },
        { key: 'source', label: 'Source', width: '100px' },
        {
          key: 'reportingDate',
          label: 'Reporting Date',
          orderBy: 'reportingDateValue',
          sortable: true,
          width: '140px',
        },
        {
          key: 'actions',
          label: 'Actions',
          width: '76px',
          overrideContents: (rowData) => {
            const metric = rowData.metric as KPIMetric;
            return (
              <IconButton
                id={`view-kpi-history-${metric.key}`}
                iconElement={<Visibility fontSize="small" />}
                size="sm"
                tooltip={`View ${metric.label} details`}
                onClick={() => loadMetricHistory(metric)}
              />
            );
          },
        },
      ],
      // Reuse the shared Policy Compliance table control so users can choose
      // which KPI detail columns are visible without changing KPI data.
      columnsDisplayConfig: {
        enabledColumnKeys,
        defaultColumnKeys: DEFAULT_KPI_DETAILS_COLUMN_KEYS,
        // Keep Actions in the shared selector so it can be restored after a
        // user hides it, without requiring a dashboard reload.
        onColumnsDisplayChange: setEnabledColumnKeys,
      },
      sortingConfig: {
        orderBy,
        order: sortOrder,
        onOrderByChange: (value) => setOrderBy(value as KPIDataGridSortKey),
        onOrderChange: setSortOrder,
      },
    }),
    [enabledColumnKeys, loadMetricHistory, orderBy, sortOrder],
  );

  const sortedData = useMemo(() => {
    // The shared DataGrid owns sort controls; KPI rows are already loaded, so
    // apply that selected order locally instead of issuing an unnecessary API call.
    return [...data].sort((left, right) => {
      const leftValue = left[orderBy];
      const rightValue = right[orderBy];
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, orderBy, sortOrder]);

  const historyData = useMemo(() => {
    if (!selectedMetric) return [];
    return selectedMetricHistory.map((point) => ({
      periodEnd: moment(point.period_end).format('MMM D, YYYY'),
      value: formatKPIValue(point.value, selectedMetric.unit),
    }));
  }, [selectedMetric, selectedMetricHistory]);
  const paginatedHistoryData = useMemo(
    () =>
      historyData.slice(
        historyPage * KPI_HISTORY_PAGE_SIZE,
        (historyPage + 1) * KPI_HISTORY_PAGE_SIZE,
      ),
    [historyData, historyPage],
  );
  const historyConfig = useMemo<DataGridConfig>(
    () => ({
      rowIdKey: 'periodEnd',
      columns: [
        { key: 'periodEnd', label: 'Reporting Date' },
        { key: 'value', label: 'Value' },
      ],
      ...(historyData.length > KPI_HISTORY_PAGE_SIZE
        ? {
            paginationConfig: {
              total_count: historyData.length,
              pageNumber: historyPage,
              pageSize: KPI_HISTORY_PAGE_SIZE,
              rowsPerPageOptions: [],
              onPageChange: setHistoryPage,
            },
          }
        : {}),
    }),
    [historyData.length, historyPage],
  );

  const exportKPIData = () => {
    setExportLoading(true);

    try {
      const exportData = sortedData.map((row) => ({
        label: row.label,
        previous: row.previous,
        current: row.current,
        change: row.change,
        trend: row.trend,
        status: getMetricStatus(row.metric).label,
        source: row.source,
        reportingDate: row.reportingDate,
      }));
      const exportColumns = KPI_EXPORT_COLUMNS;
      const escapeCSVValue = (value: string) =>
        `"${value.replace(/"/g, '""')}"`;
      const rows = exportData.map((row) =>
        exportColumns.map((column) => escapeCSVValue(String(row[column.key]))),
      );
      const csv = [
        exportColumns.map((column) => escapeCSVValue(column.label)),
        ...rows,
      ]
        .map((row) => row.join(','))
        .join('\r\n');
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(
        new Blob([csv], { type: 'text/csv' }),
      );
      link.href = objectUrl;
      link.download = 'kpi-details.csv';
      link.click();
      // Revoke the original object URL rather than the browser-normalized href
      // so every browser releases the temporary CSV resource reliably.
      URL.revokeObjectURL(objectUrl);
      showSuccessToast('KPI details exported successfully.');
    } catch (error) {
      showFailureToast(
        getKPIErrorMessage(error, 'Unable to export KPI details.'),
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <div className={styles.detailDataGridActions}>
        <IconButton
          id="export-kpi-details"
          disabled={exportLoading}
          iconClass={
            exportLoading ? 'fa fa-spinner fa-spin' : 'fa fa-file-export'
          }
          size="sm"
          tooltip={
            exportLoading ? 'Preparing KPI export' : 'Export KPI details to CSV'
          }
          onClick={exportKPIData}
        />
      </div>
      <DataGrid config={config} data={sortedData} />
      {selectedMetric && (
        <ModalDialog
          title={`${selectedMetric.label} Details`}
          subTitle={<p>Recorded values for this KPI.</p>}
          childComponent={
            <DataGrid
              config={historyConfig}
              data={paginatedHistoryData}
              loading={historyLoading}
            />
          }
          onClose={closeMetricHistory}
        />
      )}
    </>
  );
};

const KPISectionDetailContent = () => {
  const { sectionKey } = useParams<{ sectionKey: string }>();
  const location = useLocation();
  // Return to the matching dashboard layout for both internal and client users.
  const dashboardPath = location.pathname.startsWith('/client')
    ? '/client/dashboard#kpi'
    : '/admin/dashboard#kpi';
  const dashboardData = useAtomValue(kpiDashboardDataAtom);
  const section = dashboardData?.sections.find(
    (candidate) => candidate.key === sectionKey,
  );

  if (dashboardData && !dashboardData.kpi_enabled) {
    return <KPIDashboardUnavailable />;
  }

  if (!dashboardData || !section) {
    return (
      <div className={styles.detailPage}>
        <Link className={styles.detailBackLink} to={dashboardPath}>
          <ArrowBack fontSize="small" />
          Back to KPI Dashboard
        </Link>
        <Alert color="warning" className="mt-3">
          KPI data is currently unavailable.
        </Alert>
      </div>
    );
  }

  const sectionStatus = getSectionStatus(section.metrics);
  const outcomes = section.metrics.map(getMetricOutcome);
  const availableMetricCount = section.metrics.filter(
    (metric) => metric.data_status === 'AVAILABLE',
  ).length;
  return (
    <div className={styles.detailPage}>
      <Card className={`border border-light ${styles.detailMainCard}`}>
        <CardHeader className={styles.detailMainHeader}>
          <Link className={styles.detailBackLink} to={dashboardPath}>
            <ArrowBack fontSize="small" />
            Back to KPI Dashboard
          </Link>
          <div className={styles.detailHeader}>
            <div className={styles.detailIdentity}>
              <KPISectionIcon sectionKey={section.key} />
              <div>
                <h2>{section.label}</h2>
              </div>
            </div>
            <span
              className={`${styles.statusBadge} ${styles[`status${sectionStatus.replace(' ', '')}`]}`}
            >
              {sectionStatus}
            </span>
          </div>
        </CardHeader>
        <CardBody className={styles.detailMainBody}>
          <div className={styles.detailSummary}>
            <div className={styles.detailSummaryItem}>
              <span>Total Metrics</span>
              <strong>{section.metrics.length}</strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Available</span>
              <strong>
                {availableMetricCount} / {section.metrics.length}
              </strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Improving</span>
              <strong className={styles.summaryGood}>
                {outcomes.filter((outcome) => outcome === 'GOOD').length}
              </strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Worsening</span>
              <strong className={styles.summaryBad}>
                {outcomes.filter((outcome) => outcome === 'BAD').length}
              </strong>
            </div>
            <div className={styles.detailSummaryItem}>
              <span>Stable</span>
              <strong>
                {outcomes.filter((outcome) => outcome === 'NEUTRAL').length}
              </strong>
            </div>
          </div>

          <KPISectionCharts section={section} />

          <section className={styles.detailSection}>
            <header className={styles.detailDataGridHeader}>
              <h4 className="mb-0">KPI Details</h4>
            </header>
            <div className={styles.detailDataGridBody}>
              <KPISectionDataGrid
                companyId={dashboardData.company_id}
                metrics={section.metrics}
              />
            </div>
          </section>

          <div className={styles.detailUpdatedText}>
            Updated {moment(dashboardData.as_of).format('lll')}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

const KPISectionDetail = () => (
  <>
    <ModuleGuard module={ModuleKey.KPI} fallback={<KPIDashboardUnavailable />}>
      {/* Apply the same company module check to direct detail-page navigation. */}
      <KPISectionDetailContent />
    </ModuleGuard>
    {/* This detail page is a standalone route and does not inherit the dashboard's toast container. */}
    <ToastContainer />
  </>
);

export default KPISectionDetail;
